/** The full-screen panels' stylesheet, as a string.
 *
 * Same reasoning as `theme/css.ts`: the ui package is consumed by a bundler, by
 * `tsc -b` and by a DOM-less test runner, and only one of those three
 * understands `import "./x.css"`. Every custom property is read through a
 * `var(--hm-*, fallback)` so these screens still render if they are mounted
 * outside `<GameTheme>`.
 *
 * House rules, inherited from the theme and worth restating because they are
 * what separates this from a web form with a brown background:
 *
 *  - No flat rectangles. Every surface is a material gradient plus a bevel
 *    drawn with inset box-shadows plus a grain overlay.
 *  - `border-radius` is 0. The classic's frames are mitred.
 *  - Sprites are drawn at whole scales with `image-rendering: pixelated`, and
 *    every sprite box is positioned on a whole pixel.
 *  - Nothing truncates silently: a name that will not fit gets an ellipsis and
 *    keeps its full text in `title`.
 */

import { descentOf, faceOf, glyphFor } from "@game/render/text/glyphs";
import type { FaceId } from "@game/render/text/glyphs";
import { reliefDecls, reliefSwatch, toHex } from "../theme/relief.js";
import type { ReliefDepth, ReliefMaterial, ReliefRgb } from "../theme/relief.js";
import { FAINT, FDISP, G0, G1, G2, G3 } from "./cssTokens.js";

/**
 * One word of the bitmap face, as a background image.
 *
 * ## Why a stylesheet is stamping type at all
 *
 * DISMISS is the last anti-aliased serif on the town bar and it has failed
 * three rounds. Its markup belongs to `TownScreen.tsx` - a `<button>` carrying
 * an inline `font-size` - and this workstream owns `townChrome.tsx` and this
 * file and nothing else, so the FACE cannot be swapped by editing the element.
 * Measured on the shipped r2 Castle bar: the 63x14 plate interior carried 332
 * unique colours against 209 over a larger patch of the crisp `VISITING ARMY`
 * band eight pixels to its left, which is what 10px Palatino spending most of
 * every stem on partial coverage looks like.
 *
 * So the word is drawn from the same authored table `BitmapText` draws from -
 * `@game/render/text/glyphs`, the one place the face lives - as run-length
 * rectangles at `crispEdges`, and the element's own text is collapsed out of
 * the way. It is the same ink, the same tracking and the same metrics as every
 * other stamped label on the bar, and it rasterises to hard pixels for the same
 * reason they do. The durable fix is one line in `TownScreen.tsx`
 * (`<BitmapText face="text" scale={1}>DISMISS</BitmapText>`); this is what can
 * be reached from here, and it is the pixels that were wrong.
 *
 * The shadow copy is drawn one row down, which is the offset every stamped
 * label on this bar throws.
 */
const stampedWord = (
  text: string, face: FaceId, ink: string, shade: string,
): { readonly image: string; readonly w: number; readonly h: number } => {
  const f = faceOf(face);
  let w = 0;
  let descent = 0;
  for (let i = 0; i < text.length; i++) {
    if (i > 0) w += f.tracking;
    const glyph = glyphFor(text.charAt(i), face);
    w += glyph.w;
    descent = Math.max(descent, descentOf(glyph));
  }
  const h = f.capH + descent;
  let d = "";
  let pen = 0;
  for (let i = 0; i < text.length; i++) {
    const glyph = glyphFor(text.charAt(i), face);
    for (let row = 0; row < f.capH + descentOf(glyph); row++) {
      const bits = glyph.rows[row] ?? 0;
      let col = 0;
      while (col < glyph.w) {
        if ((bits & (1 << (glyph.w - 1 - col))) === 0) { col += 1; continue; }
        let run = 1;
        while (col + run < glyph.w && (bits & (1 << (glyph.w - 1 - col - run))) !== 0) run += 1;
        d += `M${String(pen + col)} ${String(row)}h${String(run)}v1h-${String(run)}z`;
        col += run;
      }
    }
    pen += glyph.w + f.tracking;
  }
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${String(w)}' `
    + `height='${String(h + 1)}' shape-rendering='crispEdges'>`
    + `<path d='${d}' fill='${shade}' transform='translate(0,1)'/>`
    + `<path d='${d}' fill='${ink}'/></svg>`;
  return {
    image: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    w,
    h: h + 1,
  };
};

const DISMISS = stampedWord("DISMISS", "text", "#c6a967", "#0a0703");

/**
 * A fixed speck field as a tiled SVG, for the one surface on the town bar that
 * had no pixels in it.
 *
 * The relief primitive builds a socket floor out of `feTurbulence` and two
 * blurred inset shadows, which is the right instrument for a large stone panel
 * and the wrong one for a 62px pocket in a hard-pixel frame: measured on the
 * shipped Castle bar a garrison socket carried 458 unique colours over 52x53
 * with a mean horizontal step of 3.1 grey levels, against 19.6 for the plank
 * beside it. That is a photograph of grit, not grit.
 *
 * The figure over it is one device pixel per mark, `crispEdges`, drawn from a
 * seeded sequence so the same board comes back in every screenshot. Two tiles
 * at coprime sizes, because a single tile at 62px would put the same speck in
 * the same corner of all 21 sockets in a row.
 */
const speckTile = (size: number, seed: number, count: number): string => {
  let s = (seed * 2654435761) >>> 0;
  const next = (): number => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return (s >>> 8) / 0x1000000;
  };
  let body = "";
  for (let i = 0; i < count; i++) {
    const x = Math.floor(next() * size);
    const y = Math.floor(next() * size);
    const w = next() > 0.74 ? 2 : 1;
    // Half the marks catch light and half are pits, or the field reads as
    // dirt on the lens rather than as a cut surface.
    const lit = next() > 0.5;
    const fill = lit ? "#fff4d8" : "#000000";
    const a = lit ? 0.10 : 0.16;
    body += `<rect x='${String(x)}' y='${String(y)}' width='${String(w)}' height='1' `
      + `fill='${fill}' fill-opacity='${String(a)}'/>`;
  }
  // Escaped whole rather than by hand: every one of #, < and the quotes has to
  // go, and a data URI edited by hand is a background nobody notices is blank.
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${String(size)}' `
    + `height='${String(size)}' shape-rendering='crispEdges'>${body}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const SOCKET_GRAIN = `${speckTile(37, 11, 150)}, ${speckTile(23, 29, 60)}`;

const lerp = (a: ReliefRgb, b: ReliefRgb, t: number): ReliefRgb => ({
  r: Math.round(a.r + (b.r - a.r) * t),
  g: Math.round(a.g + (b.g - a.g) * t),
  b: Math.round(a.b + (b.b - a.b) * t),
});

/**
 * How far the socket floor is sunk below the value the relief primitive gives
 * it, as a fraction of the way to black.
 *
 * A pocket has to be darker than what stands in it or nothing stands in it.
 * Measured on the shipped Deepvault bar the floor ran a mean luma of 40 and the
 * Magma Crawler drawn on it has a median of 40, with the Grave Rider and
 * the Doom Rider at the same figure - four of the twelve towns had a garrison
 * row in which a player could not tell what was in the socket, and the floor's
 * one-pixel speck field runs at the same frequency as the dithering in that art
 * so the two fields interleave rather than separate.
 *
 * The RAMP is untouched: every step still comes out of `reliefSwatch`, still
 * runs darkest under the top lip and lightest at the foot, and the five values
 * keep their spacing. What moves is where the whole ramp sits, and it moves
 * once for all three metals rather than per faction - the bar has no faction on
 * it to key off, and a garrison that changes value when the town does would be
 * a worse rule than a floor that is simply a floor.
 */
const SOCKET_SINK = 0.28;

const BLACK: ReliefRgb = { r: 0, g: 0, b: 0 };

/**
 * A socket floor as five flat steps, in the row's own metal.
 *
 * The three samples come from `reliefSwatch`, so the light still falls the way
 * the relief primitive says it does - darkest immediately under the
 * overhanging top lip, lightening to the foot - and a socket in the visiting
 * army's wood is still wood. What changes is that a 53-row smooth ramp under a
 * pair of Gaussian inset shadows becomes five values a pixel artist could have
 * picked, which is what the sprite standing in the socket is drawn as.
 *
 * The second layer is the left wall's cast shadow, stepped for the same
 * reason. It has to be here rather than left to the relief because this floor
 * is opaque and paints over the blurred one.
 */
const socketFloor = (material: ReliefMaterial, depth: ReliefDepth = 2): string => {
  const s = reliefSwatch("recess", material, depth);
  const sunk = (a: ReliefRgb, b: ReliefRgb, t: number): string =>
    toHex(lerp(lerp(a, b, t), BLACK, SOCKET_SINK));
  const c = [
    sunk(s.faceTop, s.faceMid, 0),
    sunk(s.faceTop, s.faceMid, 0.5),
    sunk(s.faceTop, s.faceMid, 1),
    sunk(s.faceMid, s.faceBottom, 0.5),
    sunk(s.faceMid, s.faceBottom, 1),
  ];
  return "linear-gradient(90deg, rgba(0,0,0,0.22) 0 7%, rgba(0,0,0,0.10) 7% 16%,"
    + " rgba(0,0,0,0) 16% 100%),"
    + ` linear-gradient(180deg, ${c[0]!} 0 12%, ${c[1]!} 12% 30%, ${c[2]!} 30% 52%,`
    + ` ${c[3]!} 52% 74%, ${c[4]!} 74% 100%)`;
};

export const SCREENS_STYLE_ID = "hm-screens";

const W1 = "var(--hm-wood-1, #2c1c0f)";
const W2 = "var(--hm-wood-2, #3d2716)";
const S0 = "var(--hm-stone-0, #130f0a)";
const TXT = "var(--hm-text, #efe2c2)";
const DIM = "var(--hm-text-dim, #b9a683)";
const INK = "var(--hm-ink, #241a0e)";
const BAD = "var(--hm-bad, #c04a34)";
const GOOD = "var(--hm-good, #7fbf5a)";
const GRAIN = "var(--hm-wood-grain, repeating-linear-gradient(93deg, #3d2716 0px, #54371f 2px, #3d2716 5px, #2c1c0f 6px, #54371f 11px, #3d2716 13px, #54371f 19px, #2c1c0f 21px, #3d2716 26px, #54371f 31px, #3d2716 37px))";
const STONEFACE = "var(--hm-stone-face, linear-gradient(178deg, #322d24 0%, #211d16 60%, #130f0a 100%))";

const FBODY = "var(--hm-font-body, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif)";
const FNUM = "var(--hm-font-num, 'DejaVu Sans Mono', Menlo, Consolas, monospace)";

export const SCREENS_CSS = `
/* =========================================================== screen shell */
.hs-screen {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: ${FBODY};
  color: ${TXT};
  background-color: ${S0};
  background-image:
    radial-gradient(120% 90% at 50% -10%, rgba(90,74,44,0.28) 0%, transparent 60%),
    ${STONEFACE};
  -webkit-font-smoothing: antialiased;
}
/* A screen that floats over what opened it rather than replacing it: Settings,
 * Save/Load, the Hall of Fame, Credits. The classic's options and load dialogs
 * darken the adventure map (or the title painting) and let it show through;
 * ours each mount their own castle backdrop instead, which is a different
 * picture from the one the player was just looking at.
 *
 * Only the ground is dropped. Everything a screen builds on top of it - the
 * bar, the cards, the plates - is unchanged, so a screen opts in by adding one
 * class and deleting its \`<ScreenBackdrop />\`. The scrim it floats on is
 * \`.hm-overlay\` in \`apps/web/src/styles/global.css\`, at 0.55. */
.hs-screen--sheet {
  background-color: transparent;
  background-image:
    radial-gradient(120% 90% at 50% -10%, rgba(90,74,44,0.20) 0%, transparent 62%),
    linear-gradient(178deg, rgba(19,15,10,0.34) 0%, rgba(19,15,10,0.10) 42%, rgba(19,15,10,0.52) 100%);
}
.hs-screen::after {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: var(--hm-noise, none);
  opacity: 0.05;
  mix-blend-mode: overlay;
}
.hs-screen__bar {
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
${reliefDecls("chamfer", "wood", 1)}
}
.hs-screen__body {
  position: relative;
  z-index: 2;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  overflow: hidden;
}
.hm-root--phone .hs-hero {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.hm-root--phone .hs-book,
.hm-root--phone .hm-sb__book {
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}
.hs-screen__title {
  font-family: ${FDISP};
  font-size: max(17px, var(--hm-type-floor));
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${G3};
  text-shadow: 0 1px 0 #000, 0 0 10px rgba(217,178,90,0.28);
  white-space: nowrap;
}
.hs-screen__sub { font-size: max(12px, var(--hm-type-floor)); color: ${DIM}; white-space: nowrap; }
.hs-spacer { flex: 1 1 auto; }

/* ============================================================== materials */
.hs-wood {
${reliefDecls("chamfer", "wood", 2)}
}
.hs-stone {
${reliefDecls("chamfer", "stone", 2)}
}
.hs-parch {
  color: ${INK};
${reliefDecls("chamfer", "parchment", 2)}
}
.hs-gilt-rule {
  height: 3px;
  flex: 0 0 auto;
  background-image: linear-gradient(180deg,
    rgba(0,0,0,0.8) 0 33%, ${G1} 33% 66%, rgba(255,226,170,0.26) 66% 100%);
}

/* ================================================================ sprites */
.hs-sprite { display: block; image-rendering: pixelated; flex: 0 0 auto; }
/* Downscales are nearest-neighbour too - see the note on .hm-icon--down in the kit.
   'auto' here is a bilinear ramp that gives a 50px socket sprite a thousand
   unique colours. */
.hs-sprite--down { image-rendering: pixelated; }
.hs-sprite--missing {
  background-image: repeating-linear-gradient(45deg, #3a1c1c 0 4px, #241010 4px 8px);
  box-shadow: inset 0 0 0 1px ${BAD};
}

/* ========================================================== town screen */
/* The painting COVERS this box - see stageLayout. There is no surround to
   decorate, so the frame is an inner edge rather than an outer one. */
.hs-town { position: relative; flex: 1 1 auto; min-height: 0; overflow: hidden; }
.hs-town__view { position: absolute; inset: 0; overflow: hidden; }
/* One transformed element: the whole stage is 1280x720 canvas pixels and the
   window's scale is applied once, so a slot never has to know the viewport. */
.hs-town__canvas {
  position: absolute;
  width: 1280px;
  height: 720px;
  transform-origin: 0 0;
}
.hs-town__bg { position: absolute; inset: 0; image-rendering: pixelated;
  background-repeat: no-repeat; }
/* Sun from the upper left, and a seat for the art against the wooden bars. */
.hs-town__light {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    radial-gradient(120% 90% at 22% 8%, rgba(255,238,190,0.16) 0%, rgba(255,238,190,0) 55%),
    radial-gradient(130% 105% at 50% 52%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.42) 100%);
  box-shadow:
    inset 0 2px 0 rgba(0,0,0,0.85),
    inset 0 -2px 0 rgba(0,0,0,0.85),
    inset 0 3px 0 rgba(255,225,170,0.10);
}

/* ------------------------------------------------------- streets and steps */
/* Rasterised at device resolution and scaled back down - see TownStreets. */
.hs-town__paths { position: absolute; left: 0; top: 0; pointer-events: none; }
/* Sampled off the marketplace apron the building art already stands on, so the
   street and a building's own paving are the same stone. Warm, light, low
   contrast: the old palette was cool and its mortar was nearly black, which is
   what made a road read as a masonry wall laid flat on the grass. */
.hs-cob--joint { fill: #423a2e; }
.hs-cob--t0 { fill: #a3927e; stroke: #423a2e; }
.hs-cob--t1 { fill: #7a6c5a; stroke: #423a2e; }
.hs-cob--t2 { fill: #bcab94; stroke: #423a2e; }
.hs-cob--t3 { fill: #6a5d4d; stroke: #423a2e; }
.hs-cob--t4 { fill: #93826f; stroke: #423a2e; }
.hs-cob--t5 { fill: #887a67; stroke: #423a2e; }
.hs-cob--t6 { fill: #c9b8a0; stroke: #423a2e; }
.hs-cob--t7 { fill: #5f5344; stroke: #423a2e; }
/* Every stone is a little dome: the sun is upper left, so the upper half of
   each diamond takes the light and the joint below it does the rest. */
.hs-cob--lit { fill: #f4e8d0; fill-opacity: 0.34; }
.hs-cob--dim { fill: #241d13; fill-opacity: 0.28; }
.hs-cob--worn { fill: #3a3226; fill-opacity: 0.42; }
.hs-cob--fresh { fill: #cabca6; fill-opacity: 0.20; }
.hs-cob--wet { fill: #3f3a2c; fill-opacity: 0.34; }
/* Trodden earth either side of the stones. Narrow and in two steps: paving
   that meets grass on a 1px cut is a decal, and paving inside a fifteen-pixel
   soft brown halo is an airbrush stroke in a hard-edged scene. */
.hs-town__verge-soft { fill: #5e4d2f; fill-opacity: 0.26;
  stroke: #5e4d2f; stroke-opacity: 0.20; }
.hs-town__verge { fill: #63512d; fill-opacity: 0.60;
  stroke: #63512d; stroke-opacity: 0.52; }
/* the paving fill is per band - see COBBLE_SIZE */
.hs-town__mottle { fill: url(#hs-town-mottle); }
/* Aerial perspective on the ground plane: the far lane is hazed toward the sky
   and the near one warmed, which is also what closes the seam where the painted
   hills stop and the flat lawn starts. */
.hs-town__depth { fill: url(#hs-town-depth); }
/* One sun for the whole screen, in one layer under every sprite. No blend
   mode: the SVG carries a transform, a transform is a stacking context, and
   multiply inside one has only its own siblings to blend with - which is
   exactly why three rounds of critics reported that nothing casts a shadow. */
.hs-town__shadows { pointer-events: none; }
/* Where stone meets grass. A 1px colour change is a polygon fill; a cut with a
   lit lip below it is an edge. */
.hs-town__kerb { fill: none; stroke: #3f3527; stroke-opacity: 0.55; }
/* The painted backdrop's hills stop on a hard line where the flat lawn starts.
   A band of haze across it puts the two on one ground plane. The colour is the
   distance colour of the country the town stands in, so it reads as depth on a
   lawn and as cave air underground rather than as a stripe of daylight. */
.hs-town__seam {
  position: absolute;
  left: 0; right: 0; top: 286px; height: 132px;
  pointer-events: none;
  background-image: linear-gradient(180deg,
    var(--hs-haze-0, rgba(206,214,190,0)) 0%,
    var(--hs-haze-1, rgba(206,214,190,0.21)) 42%,
    var(--hs-haze-2, rgba(190,196,160,0.12)) 68%,
    var(--hs-haze-3, rgba(150,160,110,0)) 100%);
}

/* --- one ground palette per kind of country ---------------------------- */
/* The paving geometry and the stone tones are the same everywhere; what
   changes is the light on them. A CSS filter over the ground group is the
   whole mechanism, and it is on a group of its own precisely so it cannot
   swallow the contact-shadow layer - a filter is a stacking context and
   a mix-blend-mode inside one has nothing left to multiply against. */
.hs-town__ground { filter: var(--hs-ground-tint, none); }
/* Scenery is map-object art drawn for a grass biome, so it gets the same light
   as the ground it stands on. Applied to the sprite inside the prop, because
   the prop itself already carries the band's aerial haze. */
.hs-town__prop .hs-town__art {
  filter: var(--hs-prop-tint, brightness(1))
    drop-shadow(6px 4px 0 rgba(26,19,9,0.42))
    drop-shadow(2px 1px 0 rgba(20,14,6,0.5));
}
.hs-town__canvas[data-ground="snow"] {
  --hs-prop-tint: brightness(1.04) saturate(0.5) hue-rotate(168deg);
  --hs-ground-tint: brightness(1.06) saturate(0.42) hue-rotate(178deg);
  --hs-haze-1: rgba(212,226,238,0.36); --hs-haze-2: rgba(196,212,228,0.20);
  --hs-haze-0: rgba(212,226,238,0); --hs-haze-3: rgba(196,212,228,0);
}
.hs-town__canvas[data-ground="cave"] {
  --hs-prop-tint: brightness(0.5) saturate(0.6) hue-rotate(214deg);
  --hs-ground-tint: brightness(0.44) saturate(0.55) hue-rotate(214deg);
  --hs-haze-1: rgba(96,84,132,0.26); --hs-haze-2: rgba(72,62,104,0.18);
  --hs-haze-0: rgba(96,84,132,0); --hs-haze-3: rgba(72,62,104,0);
}
.hs-town__canvas[data-ground="ash"] {
  --hs-prop-tint: brightness(0.46) saturate(1.3) hue-rotate(-20deg);
  --hs-ground-tint: brightness(0.40) saturate(1.0) hue-rotate(-18deg);
  --hs-haze-1: rgba(150,86,52,0.24); --hs-haze-2: rgba(120,64,40,0.16);
  --hs-haze-0: rgba(150,86,52,0); --hs-haze-3: rgba(120,64,40,0);
}
.hs-town__canvas[data-ground="grave"] {
  --hs-prop-tint: brightness(0.62) saturate(0.38) hue-rotate(150deg);
  --hs-ground-tint: brightness(0.62) saturate(0.42) hue-rotate(150deg);
  --hs-haze-1: rgba(128,140,140,0.24); --hs-haze-2: rgba(104,116,116,0.16);
  --hs-haze-0: rgba(128,140,140,0); --hs-haze-3: rgba(104,116,116,0);
}
.hs-town__canvas[data-ground="swamp"] {
  --hs-prop-tint: brightness(0.82) saturate(0.8) hue-rotate(22deg);
  --hs-ground-tint: brightness(0.76) saturate(0.7) hue-rotate(24deg);
  --hs-haze-1: rgba(160,172,124,0.28); --hs-haze-2: rgba(136,148,102,0.18);
  --hs-haze-0: rgba(160,172,124,0); --hs-haze-3: rgba(136,148,102,0);
}
.hs-town__canvas[data-ground="rough"] {
  --hs-prop-tint: brightness(1.0) saturate(1.15) hue-rotate(-10deg);
  --hs-ground-tint: brightness(0.98) saturate(1.3) hue-rotate(-12deg);
  --hs-haze-1: rgba(226,206,164,0.30); --hs-haze-2: rgba(206,184,142,0.20);
  --hs-haze-0: rgba(226,206,164,0); --hs-haze-3: rgba(206,184,142,0);
}
.hs-town__canvas[data-ground="desert"] {
  --hs-prop-tint: brightness(0.98) saturate(1.5) hue-rotate(-16deg);
  --hs-ground-tint: brightness(0.9) saturate(1.75) hue-rotate(-16deg);
  --hs-haze-1: rgba(232,200,152,0.32); --hs-haze-2: rgba(214,178,124,0.22);
  --hs-haze-0: rgba(232,200,152,0); --hs-haze-3: rgba(214,178,124,0);
}
/* A slot is a positioned box whose child sprite fills it exactly. The box is
   the hit target, so a building is clickable over its whole footprint. The box
   is the art's OWN size and the band's scale is applied about its bottom
   centre, so the ground line never moves when a band shrinks. */
.hs-town__slot, .hs-town__prop {
  position: absolute;
  padding: 0;
  border: 0;
  background: none;
  transform-origin: 50% 100%;
  -webkit-appearance: none;
  appearance: none;
}
.hs-town__slot { cursor: pointer; }
.hs-town__prop { pointer-events: none; }
.hs-town__slot:focus { outline: none; }
.hs-town__slot:focus-visible .hs-town__art {
  filter: drop-shadow(0 0 0 ${G3}) drop-shadow(0 0 6px ${G3});
}
/* The cast shadow, as the sprite's own silhouette offset down-right from the
   one upper-left sun. A ground blob alone cannot separate two buildings that
   overlap - and twelve Castle structures share one cobalt roof at one value,
   so from a distance the left third read as a single blue mass. An offset copy
   of the silhouette puts a dark edge between every pair of overlapping roofs
   and darkens the ground at every wall base. Zero blur: this is a hard-edged
   pixel scene and a gaussian here is the loudest thing in the frame. */
.hs-town__art {
  position: absolute;
  inset: 0;
  image-rendering: pixelated;
  background-repeat: no-repeat;
  transition: filter 90ms linear;
  filter: var(--hs-roof, brightness(1))
    drop-shadow(8px 5px 0 rgba(26,19,9,0.5))
    drop-shadow(3px 2px 0 rgba(20,14,6,0.55));
}
.hs-town__slot:hover .hs-town__art {
  filter: var(--hs-roof, brightness(1)) brightness(1.16)
    drop-shadow(7px 4px 0 rgba(26,19,9,0.42))
    drop-shadow(0 0 5px rgba(240,215,150,0.55));
}
/* An unbuilt slot shows nothing until the cursor is over it, then a ghost of
   what would stand there. It is still a real button, so it is reachable by tab. */
.hs-town__ghost .hs-town__art { opacity: 0; }
.hs-town__ghost:hover .hs-town__art,
.hs-town__ghost:focus-visible .hs-town__art {
  opacity: 0.42;
  filter: grayscale(1) brightness(1.5) drop-shadow(0 0 4px ${G3});
}
.hs-town__ghost::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 26px;
  height: 26px;
  margin-left: -13px;
  opacity: 0.55;
  background-image:
    linear-gradient(90deg, transparent 44%, ${G3} 44% 56%, transparent 56%),
    linear-gradient(0deg, transparent 44%, ${G3} 44% 56%, transparent 56%);
  filter: drop-shadow(0 1px 2px #000);
}
.hs-town__ghost:hover::after { opacity: 1; }
.hs-town__ghost--blocked::after { filter: grayscale(1) drop-shadow(0 1px 2px #000); opacity: 0.28; }

/* the hover nameplate, pinned so it can never be clipped by the stage edge */
.hs-town__plate {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  width: min(520px, 84%);
  padding: 6px 18px 7px;
  text-align: center;
  pointer-events: none;
  z-index: 40;
  box-shadow:
    inset 0 0 0 1px rgba(70,52,26,0.7),
    inset 0 0 18px rgba(120,90,45,0.35),
    0 0 0 2px ${G0},
    0 0 0 3px ${G2},
    0 4px 14px rgba(0,0,0,0.7);
}
.hs-town__plate b { font-family: ${FDISP}; font-size: max(14px, var(--hm-type-floor)); letter-spacing: 0.04em; }
.hs-town__plate span { display: block; font-size: max(12px, var(--hm-type-floor)); line-height: 1.35;
  color: #4a3a1e; }

/* ======================================================= town chrome */
/* Both bars are packed edge to edge. What this replaced had a title at the far
   left, a resource strip at the far right and about a thousand pixels of bare
   woodgrain between them - top and bottom. */
/* Fifty, not forty-six. The name plate is the tallest thing on this bar and it
   had to grow four rows to stop cutting its own sub-line's descenders (see
   TOWN_CSS); at 46 a 44px plate would have sat one pixel from both edges, which
   reads as a plate that has burst out of its bar rather than one bolted to it.
   The painting behind gives up four of its 720 rows and it COVERS its box, so
   what it gives up is crop, not scale. */
.hs-townbar { gap: 10px; height: 50px; padding: 0 12px; flex-wrap: nowrap; overflow: hidden; }
/* The readings in the middle are the part that gives way on a narrow window:
   the treasury and the three controls never wrap onto a second line and fall
   across the painting. */
.hs-townbar .hs-nameplate, .hs-townbar .hs-carve, .hs-townbar .hs-purse {
  flex: 0 0 auto;
}

/* --- the treasury: one typeface, framed icons, a legible day's income ---- */
/* --- seven pairs, not fourteen things ----------------------------------
   The strip's grouping was inverted: 11px of air BETWEEN two resources and,
   with the medallion's own 3px margin, 9px inside a pair - so a figure sat
   nearer the medallion after it than the one it belongs to. The bezel gives
   its margin up (that is 42px back across the seven, which is more than the
   wider gutter costs), the pair closes to 4 and the gutter opens to 16. */
.hs-purse {
  flex: 0 0 auto;
  display: flex; align-items: center; gap: 16px; white-space: nowrap;
}
.hs-purse__item { display: flex; align-items: center; gap: 4px; }
/* A bronze bezel round each icon: an unframed black square dropped on the
   woodgrain is the exact flat-box failure the rest of the bar avoids. */
/* The seven treasury medallions. Measured on the shipped town-castle PNG the
   interior of every one of them was exactly \`#000000\` for all 32 rows and the
   gold hairline was the identical (168,128,31) on all four edges - a hole in a
   1px border. Both halves are the same defect: the socket was a background
   colour rather than a relief, and the \`ui.*\` art is 100% opaque with its own
   black ground baked in, so whatever the socket painted the icon painted over.
   The socket is the primitive; the icon screens onto its floor. */
.hs-purse__socket {
  flex: 0 0 auto;
  display: flex;
  padding: 2px;
${reliefDecls("recess", "bronze", 2)}
}
.hs-purse__icon { image-rendering: pixelated; }
.hs-purse__body { display: flex; align-items: baseline; gap: 4px; }
.hs-purse__count {
  font-family: ${FDISP}; font-size: max(16px, var(--hm-type-floor)); color: ${G3}; letter-spacing: 0.02em;
  text-shadow: 0 -1px 0 #000, 0 1px 0 rgba(0,0,0,0.9);
}

/* --- stamped marks: a pixel figure and the shadow it throws ---------------
   These were 16-unit vector outlines stroked at 1.4 and drawn down to 9-13px,
   so the stroke landed on about one anti-aliased device pixel - and they were
   the only soft-edged marks in a frame whose type, plates and sprites are all
   hard-quantised. They are 7x7 pixel figures now, drawn at a whole cell.

   The LIGHT copy carries the shape. An engraved mark is the right idea on a
   plate a hand can reach, and it is the wrong idea at seven pixels on dark
   bronze: a dark figure on a dark ground is a smudge however crisply it is
   drawn, which is what the five plaque marks, the three section marks and the
   coin on every price plate had become. So the figure is the metal catching
   the light and the dark copy is its shadow, offset one pixel down and right
   from the screen's own upper-left sun. */
.hs-glyph { flex: 0 0 auto; display: block; }
.hs-glyph__ink { fill: #f3ddaa; }
.hs-glyph__shade { fill: rgba(10,7,3,0.72); }
/* The struck highlight, on top of the figure and only where a mark declares
   one. Two values of light on one face is what separates a coin from a pill;
   the shadow copy already gives it the rim at five o'clock. */
.hs-glyph__spec { fill: #fffaea; }

/* --- the carved control: hammered bronze in a gilt bezel, with rivets ----- */
.hs-carve {
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  /* The label group is CENTRED. \`.hs-tcontrols\` stretches its two plates to a
     176px column, and with the default flex-start the BUILD and AUTO: OFF
     groups sat about 37px left of the plate's middle with the right half of
     the screen's primary action empty. */
  justify-content: center;
  gap: 7px;
  height: 28px;
  padding: 0 14px;
  border: 0;
  cursor: pointer;
  font-family: ${FDISP};
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #f2e2b8;
  /* A plate bolted ON to the bar: lit top lip, shadowed bottom lip. It used to
     be one gold value on all four sides - sampled top and bottom of MAGE GUILD
     you got (168,128,31) twice, which is a 1px border, not a chamfer. */
${reliefDecls("chamfer", "bronze", 2)}
  text-shadow: 0 -1px 0 rgba(0,0,0,0.95), 0 1px 0 rgba(255,224,160,0.28);
}
/* Four rivets, one gradient each: the cheapest thing that says "this is a
   plate someone bolted on" rather than "this is a div". */
.hs-carve::after {
  content: "";
  position: absolute;
  inset: 4px 5px;
  pointer-events: none;
  background-repeat: no-repeat;
  background-image:
    radial-gradient(circle at 2px 2px, #f0d79a 0 1px, #2a1c07 1.2px 2px, transparent 2.2px),
    radial-gradient(circle at calc(100% - 2px) 2px, #f0d79a 0 1px, #2a1c07 1.2px 2px, transparent 2.2px),
    radial-gradient(circle at 2px calc(100% - 2px), #f0d79a 0 1px, #2a1c07 1.2px 2px, transparent 2.2px),
    radial-gradient(circle at calc(100% - 2px) calc(100% - 2px), #f0d79a 0 1px, #2a1c07 1.2px 2px, transparent 2.2px);
}
.hs-carve--wide { height: 34px; padding: 0 20px; font-size: max(13px, var(--hm-type-floor)); }
.hs-carve:hover:not(:disabled) { color: #fff4d2; filter: brightness(1.16); }
/* Pressed: the chamfer flips, because the plate is now below the surface. One
   primitive, the other shape. */
.hs-carve:active:not(:disabled) { transform: translateY(2px);
${reliefDecls("recess", "bronze", 2)}
}
.hs-carve:disabled { cursor: not-allowed; color: ${FAINT}; filter: saturate(0.3) brightness(0.7); }
/* Gilt: the same plate cast in brass, so "leave" reads as the way out and not
   as a disabled state. */
/* Planished, not combed. The tool mark was a repeating-linear-gradient at a
   1px/3px period, which at this size is not a hammered surface at all - it is
   an evenly spaced diagonal hatch, i.e. the universal convention for a
   DISABLED control, painted across the one plate the screen most wants pressed.
   Three unequal blooms instead: a highlight where this screen's sun is, a
   shadow opposite it and one smaller catch, none of them on a lattice.
   background-size and background-repeat are restated with one entry per
   layer, because a shorter list than the image list cycles. */
.hs-carve--gold { color: #2a1c05;
${reliefDecls("chamfer", "bronze", 3)}
  background-color: #b9932f;
  background-image:
    radial-gradient(38% 62% at 22% 24%, rgba(255,255,232,0.30) 0%, rgba(255,255,232,0) 62%),
    radial-gradient(30% 54% at 68% 76%, rgba(70,46,0,0.22) 0%, rgba(70,46,0,0) 66%),
    radial-gradient(22% 42% at 87% 24%, rgba(255,252,220,0.18) 0%, rgba(255,252,220,0) 70%),
    linear-gradient(180deg, #f9ecb8 0%, #dcb85e 30%, #a97f1c 70%, #6d5210 100%);
  background-size: auto, auto, auto, auto;
  background-repeat: no-repeat, no-repeat, no-repeat, no-repeat;
  text-shadow: 0 1px 0 rgba(255,250,220,0.5), 0 -1px 0 rgba(70,46,0,0.5); }
/* On a light brass face the reading inverts: the figure is the shadow in the
   cut and the lit lip is what sits under it. */
.hs-carve--gold .hs-glyph__ink { fill: #3c2a06; }
.hs-carve--gold .hs-glyph__shade { fill: rgba(255,252,225,0.66); }
.hs-carve--gold:hover:not(:disabled) { color: #2a1c05; filter: brightness(1.10); }
/* --- one primary action per screen --------------------------------------
   Gilt is how this kit says "this is the thing to press", and it was on two
   plates at once: BUILD, which is what the town screen is for, and LEAVE,
   which is the way out. They are told apart by the bar they stand in rather
   than by a second tone at the call site, because \`.hs-townbar\` already holds
   every control that is not the build column. The top bar's plate keeps a
   warmer face than the bronze beside it and gives up the gilt. */
.hs-townbar .hs-carve--gold {
  color: #f4e4bc;
  background-color: #7e5f24;
  background-image:
    radial-gradient(40% 64% at 24% 22%, rgba(255,238,196,0.16) 0%, rgba(255,238,196,0) 64%),
    linear-gradient(180deg, #9d7c33 0%, #7a5b21 44%, #4f3911 100%);
  background-size: auto, auto;
  background-repeat: no-repeat, no-repeat;
  text-shadow: 0 -1px 0 rgba(0,0,0,0.95), 0 1px 0 rgba(255,224,160,0.24);
}
.hs-townbar .hs-carve--gold .hs-glyph__ink { fill: #f3ddaa; }
.hs-townbar .hs-carve--gold .hs-glyph__shade { fill: rgba(10,7,3,0.72); }

/* --- brass plaques: the readings that fill the bars --------------------- */
/* A reading, not a control. It is CUT INTO the wood - recessed, no bezel, no
   rivets, no lit top edge and nothing standing proud of the bar - where a
   button is a plate bolted ON to it. Before this the five readouts and the
   three controls were the same rounded bevelled rectangle and nothing on the
   bar said which of them a click would do anything to. */
.hs-plaque {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  /* CENTRED, because one of the six plaques does not size itself. The five on
     the top bar shrink-wrap their own content, so this decides nothing for
     them; the TO BUILD plaque is stretched to \`.hs-tcontrols\`' 176px column,
     and flush left it left 55px of bare recess to the right of "1 left" with
     the mark and both lines packed against the plate's left lip. */
  justify-content: center;
  gap: 7px;
  /* 38, not 32. The caption is 7 glyph rows and the reading is nine at
     magnification 2 - eighteen, because the reading reserves the face's whole
     descent band so "98" and "4,000/day" are one height - so the body is 27
     rows and the two 2px lips take four more. At 32 the comma of "4,000" and
     the tail of "/day" were painted straight over the plate's own bottom
     rule on every faction. */
  height: 38px;
  padding: 0 13px;
${reliefDecls("recess", "wood", 2)}
}
.hs-plaque .hs-glyph__shade { fill: rgba(10,7,3,0.62); }
/* Caption CENTRED over reading, not both flush left. The plate is sized by
   whichever of the two is longer, so left-aligned it left 40px of bare brass
   right of RECRUITS' "98" and 45px right of BUILT's caption, and the mark
   beside them centred on the plate rather than on either line. */
.hs-plaque__body {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  /* An SVG in a flex column otherwise takes a text line box it does not use,
     and that leading is what pushed the reading onto the bottom rule. */
  line-height: 0;
  gap: 2px; min-width: 0;
}
/* The caption is an SVG now (see \`Plaque\` in townChrome.tsx). It is laid out
   as a flex row so the span is exactly the stamp's 9px box: as an inline box it
   would carry the line-height's leading and a descender gap under it, which is
   ~3px of dead height per plaque on a 32px plate. The colour is still the
   cascade's - the mark takes \`currentColor\` - and the keyline replaces the
   text-shadow that no longer applies to it. */
.hs-plaque__label {
  display: flex; align-items: center;
  color: ${DIM};
}
.hs-plaque__value {
  display: flex; align-items: center;
  color: ${G3}; white-space: nowrap;
}
.hs-tstrip {
  flex: 1 1 0;
  display: flex; align-items: center; justify-content: space-evenly;
  gap: 8px; min-width: 0; overflow: hidden;
}

/* --- the town's own name, cut into a plate ------------------------------ */
.hs-nameplate {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 44px;
  padding: 0 14px;
${reliefDecls("chamfer", "bronze", 2)}
}
.hs-nameplate__name {
  font-family: ${FDISP}; font-size: max(16px, var(--hm-type-floor)); line-height: 1;
  letter-spacing: 0.11em; text-transform: uppercase; color: ${G3};
  text-shadow: 0 -1px 0 #000, 0 1px 0 rgba(255,228,170,0.22);
}
.hs-nameplate__sub { font-size: max(10px, var(--hm-type-floor)); line-height: 1.3; color: ${DIM};
  letter-spacing: 0.05em; text-shadow: 0 1px 0 #000; }

/* ------------------------------------------------------ town army rows */
.hs-town__rows {
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
  display: flex;
  /* All three socket rows start on ONE top edge. With flex-end the market
     column - which carries a price plate the others do not - stood 15px proud
     of the garrison and the visiting army, so the bar had three top edges. */
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  height: 144px;
  padding: 4px 12px 6px;
  background-color: ${W1};
  background-image: ${GRAIN};
  /* The bar meets the painting on a gilt rail, the moulding the classic runs
     along the top of its town bar: keyline, cream lip, gold face, shade,
     groove. Six rows, the same band the hero sheet's perimeter is cut from. */
  box-shadow:
    inset 0 1px 0 #0a0704,
    inset 0 2px 0 #f2dba4,
    inset 0 4px 0 #c39a3a,
    inset 0 5px 0 #6b4d15,
    inset 0 6px 0 #0a0704,
    inset 0 -1px 0 rgba(255,225,170,0.10);
}
.hs-tarmy { display: flex; align-items: stretch; gap: 8px; min-width: 0; }
.hs-tarmy__col { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.hs-tarmy__head { display: flex; align-items: center; gap: 8px; height: 26px; min-width: 0; }
.hs-tarmy__slots { display: flex; gap: 4px; align-items: flex-start; }
/* Socket and name plate are one cell, so the seven names of a row share a
   baseline whether the socket above them is filled or empty. */
/* Fixed to the socket's own width. Without it a long name - "Bellwether
   Plated Roller", "Clockwork Sentinel" - sets the cell's intrinsic width and the
   plate grows wider than the socket it labels, which pushes the whole row. */
.hs-tcell { flex: 0 0 auto; width: 62px; display: flex; flex-direction: column;
  align-items: stretch; gap: 2px; }
/* The creature's name, on the bar, at 1x. Nothing on this screen said that the
   112-stack was High Radiants, and the FOR SALE row printed the debug tier tag
   T7 in the corner where the name belongs. */
.hs-tname {
  height: 24px;
  max-width: 100%;
  /* Two lines, because a socket is 62px and half the creature roster is wider
     than that: "Plated Bellroller" and "Clockwork Sentinel" truncate to an
     ellipsis on one line, and a name a player cannot finish reading is barely
     better than the tier tag it replaced. */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: break-word;
  text-align: center;
  font-family: ${FDISP};
  font-size: max(9.5px, var(--hm-type-floor));
  line-height: 12px;
  letter-spacing: 0.01em;
  color: #e8d3a0;
  /* Sampled top hairline and bottom hairline of this plate and got the same
     (107,77,21) twice - a border wearing a chamfer's name. */
${reliefDecls("chamfer", "bronze", 1)}
  text-shadow: 0 1px 0 rgba(0,0,0,0.9);
}
/* A plate with nothing stamped in it is not a plate. Three of the seven
   sockets on a Castle, Gravewatch, Frostmark or Prismgate garrison are empty, and
   each of them drew a fully lit chamfered gold plate carrying no name - which
   reads as a label the build failed to fill in. The cell keeps its height, so
   the row still has one baseline, and gives up the gilt. */
.hs-tname--empty {
  position: relative;
${reliefDecls("recess", "stone", 1)}
}
.hs-tlabel {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: ${FDISP}; font-size: max(11px, var(--hm-type-floor)); letter-spacing: 0.14em;
  text-transform: uppercase; color: ${G3}; white-space: nowrap;
  text-shadow: 0 1px 0 #000;
}
.hs-thint { font-size: max(10px, var(--hm-type-floor)); color: ${FAINT}; white-space: nowrap; font-style: italic; }
/* --- the one control that lives in a row's head -------------------------
   DISMISS was the last typeset string on this bar and it is stamped now, from
   the same authored table as every other label here - see \`stampedWord\` above
   for why a stylesheet is drawing type and what the durable fix in
   \`TownScreen.tsx\` would be. The element's own text stays in the DOM for the
   accessibility tree and is collapsed to nothing visually; \`!important\` is the
   only reach past the inline \`font-size\` that markup carries, and it is doing
   exactly one job.

   The PLATE is a cut, not a chamfer. Given a lit bronze bezel one round it made
   a DESTRUCTIVE action the brightest object in the VISITING ARMY group -
   brighter than the section header it stands beside. Dismissing the hero is the
   one thing on this bar that should read as recessive, and a cut also puts its
   label the way round every other label here is: pale ink on dark ground. */
.hs-tarmy__head .hs-btn {
  display: flex; align-items: center; justify-content: center;
  height: 17px;
  font-size: 0 !important;
  line-height: 0;
  text-shadow: none;
${reliefDecls("recess", "wood", 1)}
  background-image: ${socketFloor("wood", 1)};
  background-size: auto, auto;
  background-repeat: no-repeat, no-repeat;
}
.hs-tarmy__head .hs-btn::before {
  content: "";
  display: block;
  flex: 0 0 auto;
  width: ${String(DISMISS.w)}px;
  height: ${String(DISMISS.h)}px;
  background-image: ${DISMISS.image};
  background-repeat: no-repeat;
  image-rendering: pixelated;
}
.hs-tarmy__head .hs-btn:hover:not(:disabled) { filter: brightness(1.25); }
.hs-tdiv {
  flex: 0 0 auto; align-self: stretch; width: 3px; margin: 2px 4px;
  background-image: linear-gradient(180deg, ${G0}, ${G2} 45%, ${G0});
  box-shadow: 1px 0 0 rgba(0,0,0,0.7), -1px 0 0 rgba(0,0,0,0.7);
}
.hs-tcontrols {
  flex: 0 0 auto;
  align-self: stretch;
  display: flex; flex-direction: column; align-items: stretch;
  justify-content: flex-end; gap: 6px; min-width: 176px;
}

/* the visiting hero: a face and a name, not a bare string */
.hs-herotab {
  display: inline-flex; align-items: center; gap: 8px;
  height: 26px; padding: 0 10px 0 3px; border: 0; cursor: pointer;
  color: inherit; text-align: left;
${reliefDecls("chamfer", "bronze", 1)}
}
.hs-herotab--none { cursor: default; opacity: 0.8; }
.hs-herotab__slot {
  flex: 0 0 auto; width: 22px; height: 22px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  isolation: isolate;
${reliefDecls("recess", "stone", 1)}
}
.hs-herotab__slot .hm-icon { image-rendering: pixelated; }
.hs-herotab__text { display: flex; flex-direction: column; gap: 2px;
  line-height: 0; min-width: 0; }
.hs-herotab__text b { display: flex; color: ${G3}; }
.hs-herotab__text i { display: flex; font-style: normal; color: ${DIM}; }

/* --- three rows, three metals -------------------------------------------
   Garrison is the town's own steel, the visiting army is warmer because it
   travels with the hero, and the market is brass with a price under it. Before
   this the only difference between "troops you own" and "troops you can buy"
   was which side of the bar they were on. */
/* One cut, three materials: the row's metal is a parameter of the primitive,
   not a second recipe. */
.hs-tarmy--hero .hs-tslot {
${reliefDecls("recess", "wood", 2)}
}
.hs-tarmy--market .hs-tslot {
${reliefDecls("recess", "bronze", 2)}
}
/* Only the image changes: the tile sizes and repeats above are per-property
   and cascade on their own. */
.hs-tarmy--hero .hs-tslot::before {
  background-image: ${SOCKET_GRAIN}, ${socketFloor("wood")};
}
.hs-tarmy--market .hs-tslot::before {
  background-image: ${SOCKET_GRAIN}, ${socketFloor("bronze")};
}
/* A market socket carries a price, so its number can never be read as a
   stack size the way a bare "17" beside the garrison's "17" was. */
.hs-tbuy { display: flex; flex-direction: column; align-items: stretch; gap: 2px; }
.hs-tbuy__price {
  display: flex; align-items: center; justify-content: center; gap: 3px;
  height: 14px;
  font-family: ${FDISP}; font-size: max(10px, var(--hm-type-floor)); letter-spacing: 0.04em;
  color: #f0d590;
${reliefDecls("chamfer", "bronze", 1)}
  text-shadow: 0 1px 0 #000;
}
.hs-tbuy__price .hs-glyph__ink { fill: #f7e5ae; }
.hs-tbuy__price .hs-glyph__shade { fill: rgba(10,7,3,0.66); }

/* --- the visiting hero: a plate the height of the bar -------------------- */
.hs-heroplate {
  flex: 0 0 auto;
  display: flex; align-items: center; gap: 9px;
  padding: 3px 10px 3px 3px; border: 0; cursor: pointer;
  color: inherit; text-align: left;
${reliefDecls("chamfer", "bronze", 2)}
}
.hs-heroplate--none { cursor: default; opacity: 0.75; }
/* The full height of the text column beside it, and the portrait FILLS it.
   Sized to the column but holding a 58px square picture, the niche carried 34
   rows of bare quantised stone - 17 above the head and 17 below the shoulders,
   37% of the frame - which is the same void the pip grid was added to close,
   wearing the floor instead of the brass. \`overflow: hidden\` is what crops the
   square source to the niche's shape; see \`HeroPlate\`. */
.hs-heroplate__frame {
  position: relative;
  flex: 0 0 auto; width: 60px; height: 92px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  isolation: isolate;
${reliefDecls("recess", "stone", 2)}
}
/* The niche is wall the portrait does not cover, so it takes the socket's
   quantised floor for the same reason the sockets did. */
.hs-heroplate__frame::before {
  content: "";
  position: absolute;
  inset: 2px;
  /* BEHIND the portrait, and the portrait is a static span: a positioned
     pseudo-element paints over in-flow content, so without this the niche
     wall covered the face it exists to sit behind. \`isolation\` above keeps
     the negative layer inside this frame. */
  z-index: -1;
  pointer-events: none;
  background-image: ${SOCKET_GRAIN}, ${socketFloor("stone")};
  background-size: 37px 37px, 23px 23px, auto, auto;
  background-repeat: repeat, repeat, no-repeat, no-repeat;
  image-rendering: pixelated;
}
/* \`flex: 0 0 auto\`, because the portrait is WIDER than the niche it fills and a
   flex item's default is to shrink to fit: without it a 92px picture is
   squeezed to 60 and the hero is drawn a third narrower than he was painted. */
.hs-heroplate__frame .hm-icon { image-rendering: pixelated; flex: 0 0 auto; }
/* Stamped, like everything else on this bar. The visiting hero's name and
   class were the last anti-aliased serif on the bottom bar - eight pixels from
   twenty-one hard-quantised creature plates - and a face that is not the
   screen's face is what makes one block read as a different program. Both
   lines are flex rows so the SVG does not take a text line box it never uses,
   which is also what keeps the two lines' leading at the 3px named here. */
.hs-heroplate__text { display: flex; flex-direction: column; gap: 3px;
  justify-content: center; line-height: 0; min-width: 0; }
.hs-heroplate__text b { display: flex; color: ${G3}; }
.hs-heroplate__text i { display: flex; font-style: normal; color: ${DIM}; }
/* --- the four primary skills, and the two readings a hero is spent on -----
   The void this fills was 105x55 of unmarked gold to the right of the portrait
   and above the name, on the biggest and brightest field on the bar. Heroes
   III carries the primary-skill boxes in exactly this footprint.

   \`width: 0\` with a percentage minimum is the whole trick and it is here for a
   reason: a flex item with a definite width contributes that width - zero - to
   its column's max-content, so the plate is still sized by the hero's name and
   class line and the seven sockets to its right did not move a pixel. The
   minimum then stretches the grid back out to the column it is in.

   Cut in, not bolted on: six lit chamfers inside a lit plate would be the
   DISMISS mistake at six times the size. */
.hs-heropips {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 3px; width: 0; min-width: 100%; margin-top: 4px;
}
.hs-heropip {
  display: flex; align-items: center; gap: 3px; min-width: 0; overflow: hidden;
  height: 17px; padding: 0 3px; color: ${TXT};
${reliefDecls("recess", "bronze", 1)}
}
.hs-heropip .hs-glyph__ink { fill: #e8cf94; }
.hs-heropip .hs-glyph__shade { fill: rgba(8,5,2,0.72); }

/* --- a creature socket: recessed metal, one baseline, one cap height ----
   The light in this world falls from above, and both halves of that were
   backwards here. The FLOOR ran bright at the top and faded to the darkest
   pixel on the screen at the foot, which is a dome lit from inside, or a hole
   punched through the bar - not a pocket cut into it. A cut pocket is darkest
   directly under its own top lip, because that lip shadows it, and lightens
   toward the bottom where the light finally reaches the floor.

   The LIP was a single uniform gold hairline on all four sides: the same
   (105,78,24) at the top edge and at the bottom edge, which is a 1px CSS
   border however it is coloured. A chamfer has a lit top and a shadowed
   bottom, so the ring is now three values, not one. */
.hs-tslot {
  position: relative;
  width: 62px;
  height: 62px;
  padding: 0;
  border: 0;
  cursor: pointer;
  isolation: isolate;
${reliefDecls("recess", "stone", 2)}
  overflow: hidden;
}
/* --- the floor is pixels, not a photograph of one -----------------------
   The relief's own floor is fractal noise under two Gaussian inset shadows,
   which is the right instrument for a large stone panel and the wrong one for
   a 62px pocket in a hard-pixel frame: measured on the shipped Castle bar a
   garrison socket carried 458 unique colours over 52x53 at a mean horizontal
   step of 2.9 grey levels, against 145 and 3.7 for the plank beside it. That
   is a photograph of grit, and it is most exposed in the six empty sockets
   where no creature covers it.

   So the interior is repainted: five flat steps of the row's own metal, a
   stepped wall shadow, and a one-device-pixel speck field over both. It is
   opaque, which is what lets it stand in for the two blurred shadows as well
   as for the ramp - they fall inside the 2px lips this sits on top of.

   The LIPS are still the relief's, all four of them, and so is the material:
   \`socketFloor\` reads its three values out of \`reliefSwatch\`, so the light
   still falls the way that module decides it does and a change there still
   reaches here. Painted by \`::before\`, the socket's first child, so the
   creature and the count plinth both stand in front of it. */
.hs-tslot::before {
  content: "";
  position: absolute;
  inset: 2px;
  z-index: 0;
  pointer-events: none;
  background-image: ${SOCKET_GRAIN}, ${socketFloor("stone")};
  background-size: 37px 37px, 23px 23px, auto, auto;
  background-repeat: repeat, repeat, no-repeat, no-repeat;
  image-rendering: pixelated;
}
.hs-tslot--empty { cursor: default; }
/* The empty-socket "+".
 *
 * The inset used to be a fixed 26px. A socket is ~62px at 1080p, so 26px of inset
 * left a 10px cross; at 1366 the socket shrinks to ~52px and the same inset left
 * NOTHING - the two gradient bands collapsed to zero width and the affordance
 * silently disappeared, so an empty garrison slot at that width was
 * indistinguishable from a socket the game had simply not drawn. A proportional
 * inset keeps the mark in the same relationship to its socket at any size. */
.hs-tslot--empty::after {
  content: "";
  position: absolute;
  left: 50%; top: 50%;
  width: 14px; height: 14px;
  transform: translate(-50%, -50%);
  background-image:
    linear-gradient(90deg, transparent 0 6px, rgba(146,110,38,0.55) 6px 8px, transparent 8px),
    linear-gradient(0deg, transparent 0 6px, rgba(146,110,38,0.55) 6px 8px, transparent 8px);
}
.hs-tslot--drop { outline: 2px solid ${G3}; outline-offset: -2px; }
.hs-tslot--illegal { outline: 2px solid ${BAD}; outline-offset: -2px; }
.hs-tslot--dragging { opacity: 0.35; }
.hs-tslot:hover { filter: brightness(1.3); }
/* Bottom-aligned: every creature in the row stands on one floor, whatever its
   frame is, which is what stops the row reading as ragged. */
.hs-tslot__art {
  position: absolute; left: 50%; bottom: 5px;
  transform: translateX(-50%);
  image-rendering: pixelated;
  background-repeat: no-repeat;
}
/* --- the plinth every socket stands on ---------------------------------
   Drawn on an EMPTY socket too, and that is the whole rule. It used to be
   rendered only where there was a count, so the three empty sockets of a
   Castle garrison had 13 more rows of recess floor than their neighbours and
   the row carried two different bottom lines. An empty plinth is the socket's
   own floor - a shallow cut, not the lit brass a reading stands on - so it
   holds the line without claiming to say anything. */
.hs-tslot__count {
  position: absolute; left: 0; right: 0; bottom: 0;
  height: 15px;
  display: flex; align-items: center; justify-content: center;
  padding: 0 4px;
  color: ${TXT};
${reliefDecls("chamfer", "bronze", 1)}
}
.hs-tslot__count--empty {
${reliefDecls("recess", "stone", 1)}
}
/* The empty column's two shallow cuts take the same quantised floor as the
   socket above them, or the three plates of an empty cell are one pixel
   figure over two grey clouds. */
.hs-tslot__count--empty::before,
.hs-tname--empty::before {
  content: "";
  position: absolute;
  inset: 1px;
  pointer-events: none;
  background-image: ${SOCKET_GRAIN}, ${socketFloor("stone", 1)};
  background-size: 37px 37px, 23px 23px, auto, auto;
  background-repeat: repeat, repeat, no-repeat, no-repeat;
  image-rendering: pixelated;
}

/* --- 1366x768: the same bar, on two thirds of the width -----------------
   Three seven-slot rows, a hero plate and the build controls do not fit a
   1366px bar at 62px a socket, and what "does not fit" looked like was the
   seventh dwelling sliding under the BUILD plate. Everything in the bar steps
   down together rather than one block being clipped. */
@media (max-width: 1600px) {
  .hs-town__rows { gap: 4px; padding: 4px 8px 6px; height: 132px; }
  .hs-tslot { width: 46px; height: 54px; }
  .hs-tcell { width: 46px; }
  .hs-tname { height: 22px; line-height: 11px; font-size: max(8.5px, var(--hm-type-floor)); }
  .hs-tarmy__slots { gap: 3px; }
  .hs-tarmy { gap: 6px; }
  .hs-tslot__count { min-width: 20px; font-size: max(10px, var(--hm-type-floor)); line-height: 13px; padding: 0 3px; }
  .hs-tbuy__price { height: 12px; font-size: max(9px, var(--hm-type-floor)); gap: 2px; }
  .hs-heroplate { gap: 6px; padding: 3px 8px 3px 3px; }
  .hs-heroplate__frame { width: 44px; height: 72px; }
  .hs-heroplate__text b { font-size: max(13px, var(--hm-type-floor)); }
  .hs-heroplate__text i { font-size: max(10px, var(--hm-type-floor)); }
  .hs-heropips { gap: 1px; margin-top: 3px; }
  .hs-heropip { height: 13px; gap: 2px; padding: 0 2px; }
  .hs-tcontrols { min-width: 138px; gap: 4px; }
  .hs-tdiv { margin: 2px 1px; }
  .hs-purse { gap: 10px; }
  .hs-purse__item { gap: 3px; }
  .hs-purse__count { font-size: max(14px, var(--hm-type-floor)); }
  .hs-purse__icon { margin: 0 1px; }
  /* 32px medallions and their counts are untouched; what goes is the air
     around the bezel, which the wide layout has already given up entirely. */
  /* The top bar is the block with no slack left at this width, and what the
     plaque strip gets is whatever the nameplate, the treasury and the three
     controls leave it. The nameplate is the part that varies: it holds the
     town's own name, so the strip's room swings ~65px between \`Castle\` and
     \`Ironhorde\`, and the first cut of this fix fitted eleven towns and
     missed the twelfth by a single pixel. So the air between plates steps down
     across the whole bar, not just inside the strip - measured after, the
     narrowest of the twelve (Ironhorde) has 40px in hand. No reading gives up
     a magnification to pay for it; nothing here touches a glyph size. */
  .hs-townbar { gap: 8px; padding: 0 10px; }
  .hs-plaque { padding: 0 5px; gap: 4px; }
  .hs-plaque__value { font-size: max(13px, var(--hm-type-floor)); }
  .hs-tstrip { gap: 5px; }
  .hs-carve { padding: 0 8px; }
}

/* --- the build list ----------------------------------------------------- */
.hs-buildrow {
  display: flex; align-items: center; gap: 10px; width: 100%;
  margin-bottom: 6px; padding: 7px 10px; border: 0; cursor: pointer;
  text-align: left; color: inherit;
${reliefDecls("chamfer", "wood", 1)}
}
.hs-buildrow:hover { filter: brightness(1.2); }
.hs-buildrow--blocked { filter: saturate(0.5) brightness(0.82); }
.hs-buildrow__art { flex: 0 0 auto; image-rendering: pixelated; }
.hs-buildrow__body { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1 1 auto; }
.hs-buildrow__name { font-family: ${FDISP}; font-size: max(14px, var(--hm-type-floor)); color: ${G3};
  letter-spacing: 0.04em; text-shadow: 0 1px 0 #000; }

/* =========================================================== army slots */
.hs-slot {
  position: relative;
  width: 62px;
  height: 62px;
  padding: 0;
  border: 0;
  cursor: pointer;
  isolation: isolate;
${reliefDecls("recess", "stone", 2)}
  overflow: hidden;
}
/* An empty socket is the same cut as a full one: dimming it turns the recess
   back into a flat hole. */
.hs-slot--empty { cursor: default; }
.hs-slot--drop { outline: 2px solid ${G3}; outline-offset: -2px; }
.hs-slot--illegal { outline: 2px solid ${BAD}; outline-offset: -2px; cursor: not-allowed; }
.hs-slot--dragging { opacity: 0.35; }
.hs-slot__art {
  position: absolute; left: 50%; top: 50%;
  transform: translate(-50%, -50%);
  image-rendering: pixelated;
  background-repeat: no-repeat;
}
.hs-slot__count {
  position: absolute; right: 0; bottom: 0;
  min-width: 24px;
  padding: 0 3px;
  font-family: ${FNUM};
  font-size: max(11px, var(--hm-type-floor));
  line-height: 14px;
  text-align: right;
  color: ${TXT};
${reliefDecls("chamfer", "bronze", 1)}
}
.hs-slot__mark {
  position: absolute; left: 2px; top: 1px;
  font-size: max(9px, var(--hm-type-floor)); letter-spacing: 0.08em; color: ${G3};
  text-shadow: 0 1px 0 #000;
}

/* ============================================================== dialogs */
.hs-scrim {
  position: absolute; inset: 0; z-index: 50;
  display: flex; align-items: center; justify-content: center;
  background: radial-gradient(60% 60% at 50% 45%, rgba(0,0,0,0.55), rgba(0,0,0,0.82));
}
.hs-dialog {
  position: relative;
  display: flex;
  flex-direction: column;
  max-width: min(760px, 92vw);
  max-height: 88vh;
  padding: 14px 16px 12px;
  background-color: ${W2};
  background-image: ${GRAIN};
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px ${G2},
    inset 0 0 0 3px ${G0},
    inset 0 0 40px rgba(0,0,0,0.55),
    0 10px 40px rgba(0,0,0,0.8);
}
.hs-dialog__title {
  font-family: ${FDISP};
  font-size: max(16px, var(--hm-type-floor));
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${G3};
  text-shadow: 0 1px 0 #000;
  margin-bottom: 8px;
}
.hs-dialog__body { overflow-y: auto; min-height: 0; padding-right: 2px; }
.hs-dialog__foot { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(0,0,0,0.6);
  box-shadow: inset 0 1px 0 rgba(255,226,170,0.12); }
/* The dialog's own Close. It is the one control every dialog in the build has,
   so it is the one that most needs not to look like a browser button: struck
   bronze rather than the wood the other footer buttons are cut from, with the
   same rivets the \`.hs-plate\` kit uses, because the last thing a player reads
   on a modal should be an object rather than a link. Declared here rather than
   in \`polish.ts\` because a \`ScreenDialog\` is opened over screens that never
   mount the polish sheet - the town's garrison and the adventure map's own
   prompts among them. */
.hs-dialog__close {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 6px 18px;
  color: #2a1c06;
${reliefDecls("chamfer", "bronze", 2)}
  text-shadow: 0 1px 0 rgba(255,255,255,0.36);
}
.hs-dialog__close::before, .hs-dialog__close::after {
  content: "";
  width: 5px; height: 5px;
  flex: 0 0 auto;
  transform: rotate(45deg);
  background-image: linear-gradient(135deg, #fff0c4, ${G2} 55%, #3f2b0c);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.75);
}
.hs-dialog__close:hover:not(:disabled) { color: #2a1c06; filter: brightness(1.14); }

/* ============================================================== buttons */
.hs-btn {
  position: relative;
  flex: 0 0 auto;
  padding: 5px 14px;
  border: 0;
  cursor: pointer;
  font-family: ${FDISP};
  font-size: max(12px, var(--hm-type-floor));
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${TXT};
${reliefDecls("chamfer", "wood", 2)}
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
}
.hs-btn:hover:not(:disabled) { filter: brightness(1.18); color: #fff2d0; }
.hs-btn:active:not(:disabled) { transform: translateY(1px);
${reliefDecls("recess", "wood", 2)}
}
.hs-btn:disabled { cursor: not-allowed; color: ${FAINT}; filter: saturate(0.35) brightness(0.75); }
.hs-btn--gold { color: #221806;
${reliefDecls("chamfer", "bronze", 3)}
  text-shadow: 0 1px 0 rgba(255,255,255,0.28); }
.hs-btn--gold:hover:not(:disabled) { color: #221806; filter: brightness(1.12); }
.hs-btn--ghost { background-image: none; background-color: rgba(0,0,0,0.25); }

/* ================================================================ tables */
.hs-cost { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.hs-cost__item { display: flex; align-items: center; gap: 4px;
  font-family: ${FNUM}; font-size: max(13px, var(--hm-type-floor)); }
.hs-cost__item--short { color: ${BAD}; }
.hs-cost__item--ok { color: ${TXT}; }

.hs-req { display: flex; align-items: center; gap: 6px; font-size: max(12px, var(--hm-type-floor)); padding: 1px 0; }
.hs-req--met { color: ${GOOD}; }
.hs-req--unmet { color: ${BAD}; }
.hs-req__tick { width: 12px; text-align: center; font-family: ${FNUM}; }

.hs-why {
  margin-top: 10px;
  padding: 6px 10px;
  font-size: max(12px, var(--hm-type-floor));
  line-height: 1.4;
}
.hs-why b { color: #5a2417; }

/* ========================================================== hero screen */
.hs-hero { display: grid; grid-template-columns: 300px 1fr 300px; gap: 10px;
  padding: 10px; width: 100%; min-height: 0; }
.hs-hero__col { display: flex; flex-direction: column; gap: 8px; min-height: 0; min-width: 0; }
.hs-card { padding: 8px 10px; }
.hs-card__title {
  font-family: ${FDISP}; font-size: max(12px, var(--hm-type-floor)); letter-spacing: 0.11em;
  text-transform: uppercase; color: ${G3}; margin-bottom: 6px;
  text-shadow: 0 1px 0 #000;
}
/* Every portrait on every screen, not just the hero sheet.
 *
 * What was here was one flat band: a 4px ring of a single colour, rgb(107,77,21)
 * - 804 pixels of it across the top, 804 down the left, 798 down the right,
 * mean luminance 51.8 top against 52.4 bottom, a spread of 0.6, and 39 unique
 * colours in the whole ring. The largest object on the sheet was framed in a
 * strip of card that could not say where the light was, on a screen where every
 * other edge goes through the relief primitive.
 *
 * The hero sheet fixed that for itself. This is the same treatment promoted to
 * the shared rule, because the town screen draws six of these and the tavern
 * and garrison dialogs draw more: a carved frame whose face is a gradient
 * across the diagonal (bright top left, dark bottom right), a black keyline
 * outside it, four corner rivets struck into the frame itself, and an ::after
 * that carries the inner keyline and the vignette. The vignette paints over the
 * portrait on purpose - a socket whose floor is flat to its edges reads as a
 * printed tile, and the darkening at the corners is what sets it into the board.
 *
 * The rivets are drawn in \`background-image\` rather than as pseudo-elements
 * because ::before and ::after are both spoken for, and because a rivet in the
 * background lands on the frame instead of over the face.
 */
.hs-portrait {
  position: relative;
  align-self: center;
  display: block;
  width: -moz-fit-content;
  width: fit-content;
  margin: 0 auto;
  padding: var(--hs-portrait-frame, 12px);
  background-color: #0b0805;
  background-image:
    radial-gradient(circle at 7px 7px,
      #f7e6bb 0 1.5px, #b48c3e 1.5px 3px, rgba(0,0,0,0.9) 3px 4.2px, rgba(0,0,0,0) 4.2px),
    radial-gradient(circle at calc(100% - 7px) 7px,
      #f7e6bb 0 1.5px, #b48c3e 1.5px 3px, rgba(0,0,0,0.9) 3px 4.2px, rgba(0,0,0,0) 4.2px),
    radial-gradient(circle at 7px calc(100% - 7px),
      #f7e6bb 0 1.5px, #b48c3e 1.5px 3px, rgba(0,0,0,0.9) 3px 4.2px, rgba(0,0,0,0) 4.2px),
    radial-gradient(circle at calc(100% - 7px) calc(100% - 7px),
      #f7e6bb 0 1.5px, #b48c3e 1.5px 3px, rgba(0,0,0,0.9) 3px 4.2px, rgba(0,0,0,0) 4.2px),
    linear-gradient(146deg,
      #f2dca6 0%, #d3b169 13%, #a98338 34%, #7a5a20 58%, #4e380f 80%, #2c1f08 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.92),
    0 6px 18px rgba(0,0,0,0.8),
    0 1px 0 rgba(255,232,182,0.10);
}
.hs-portrait::after {
  content: "";
  position: absolute;
  inset: calc(var(--hs-portrait-frame, 12px) - 3px);
  pointer-events: none;
  box-shadow:
    0 0 0 1px rgba(0,0,0,0.95),
    0 0 0 3px rgba(0,0,0,0.55),
    inset 0 1px 0 rgba(0,0,0,0.85),
    inset 0 0 26px rgba(0,0,0,0.55),
    inset 0 -14px 26px rgba(0,0,0,0.35);
}
/* A portrait small enough that a 12px frame would be most of the object: the
   thumbnail in a list row, a tavern card, the town's garrison strip. One
   custom property moves the frame and its keyline together. */
.hs-portrait--sm { --hs-portrait-frame: 7px; }
.hs-hero__name { font-family: ${FDISP}; font-size: max(18px, var(--hm-type-floor)); text-align: center;
  color: ${G3}; text-shadow: 0 1px 0 #000; }
.hs-hero__class { font-size: max(12px, var(--hm-type-floor)); text-align: center; color: ${DIM}; }

.hs-primaries { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; }
.hs-primary { display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 4px 2px; }
.hs-primary__value { font-family: ${FNUM}; font-size: max(20px, var(--hm-type-floor)); color: ${TXT};
  text-shadow: 0 1px 0 #000, 0 0 8px rgba(217,178,90,0.25); }
.hs-primary__label { font-size: max(9px, var(--hm-type-floor)); letter-spacing: 0.08em; text-transform: uppercase;
  color: ${FAINT}; }

.hs-skills { display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px; }
.hs-skill { display: flex; align-items: center; gap: 6px; padding: 3px 4px; min-width: 0; }
.hs-skill__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.hs-skill__name { font-size: max(12px, var(--hm-type-floor)); color: ${TXT}; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; }
.hs-skill__level { font-size: max(10px, var(--hm-type-floor)); letter-spacing: 0.08em; text-transform: uppercase;
  color: ${G3}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* Five columns that share the panel rather than overflowing it: at 1366x768
   the right column is 300px wide, so a fixed 68px socket would push the last
   column off the screen. */
.hs-doll { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 5px;
  justify-items: stretch; }
.hs-doll .hs-slot { width: 100%; max-width: 68px; height: auto; aspect-ratio: 1 / 1;
  justify-self: center; }
.hs-backpack { display: grid; grid-template-columns: repeat(8, 1fr); gap: 4px; }
.hs-backpack .hs-slot { width: 100%; aspect-ratio: 1 / 1; height: auto; }

.hs-bar { position: relative; height: 16px;
${reliefDecls("recess", "stone", 1)}
}
.hs-bar__fill { position: absolute; inset: 1px auto 1px 1px;
  background-image: linear-gradient(180deg, ${G3} 0%, ${G1} 60%, ${G0} 100%); }
.hs-bar__text { position: absolute; inset: 0; display: flex; align-items: center;
  justify-content: center; font-family: ${FNUM}; font-size: max(11px, var(--hm-type-floor)); letter-spacing: 0;
  color: #fff3d6;
  text-shadow: 0 1px 2px #000; }

/* =========================================================== spell book */
.hs-book { display: flex; align-items: center; justify-content: center;
  flex: 1 1 auto; min-height: 0; padding: 12px; gap: 0; }
.hs-book__spread { display: flex; min-height: 0; max-height: 100%;
  box-shadow: 0 14px 46px rgba(0,0,0,0.85); }
.hs-book__page {
  position: relative;
  width: 366px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${INK};
  /* The kit's own parchment, figure and all, rather than the flat radial this
     used to name. --hm-parchment is a three-stop gradient: it carries the
     sheet's colour and none of its content, so the leaf under the ruled border
     was a painted rectangle. The relief primitive already ships laid lines at
     irregular spacing, chain lines at irregular spacing and foxed patches on
     no lattice; the page keeps its own box-shadow by restating it after. */
${reliefDecls("chamfer", "parchment", 1)}
  box-shadow: inset 0 0 0 1px rgba(70,52,26,0.75), inset 0 0 34px rgba(120,88,42,0.4);
}
.hs-book__page--left { box-shadow: inset 0 0 0 1px rgba(70,52,26,0.75),
  inset -18px 0 26px rgba(80,58,26,0.45), inset 0 0 34px rgba(120,88,42,0.35); }
.hs-book__page--right { box-shadow: inset 0 0 0 1px rgba(70,52,26,0.75),
  inset 18px 0 26px rgba(80,58,26,0.45), inset 0 0 34px rgba(120,88,42,0.35); }
.hs-book__gutter { width: 16px; align-self: stretch;
  background-image: linear-gradient(90deg, #5a4322, #2b1e0d 45%, #2b1e0d 55%, #5a4322); }
.hs-book__head { font-family: ${FDISP}; font-size: max(13px, var(--hm-type-floor)); letter-spacing: 0.12em;
  text-transform: uppercase; color: #533c15; text-align: center; }
.hs-book__list { display: flex; flex-direction: column; gap: 6px;
  flex: 1 1 auto; min-height: 0; }
.hs-spell {
  display: flex; align-items: center; gap: 9px;
  padding: 4px 6px; border: 0; cursor: pointer; text-align: left; width: 100%;
  color: ${INK};
  background: linear-gradient(180deg, rgba(255,245,215,0.42), rgba(140,110,60,0.10));
  box-shadow: inset 0 0 0 1px rgba(90,68,32,0.35);
}
.hs-spell:hover:not(:disabled) { box-shadow: inset 0 0 0 1px #6b4d15,
  0 0 8px rgba(107,77,21,0.35); }
.hs-spell:disabled { cursor: not-allowed; filter: grayscale(0.85) contrast(0.85); opacity: 0.6; }
.hs-spell__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.hs-spell__name { display: block; font-family: ${FDISP}; font-size: max(13px, var(--hm-type-floor)); }
.hs-spell__meta { display: block; font-size: max(11px, var(--hm-type-floor)); color: #5f4a22; }
.hs-spell__cost { margin-left: auto; font-family: ${FNUM}; font-size: max(14px, var(--hm-type-floor));
  color: #22406e; }
.hs-spell__cost--short { color: #8a2418; }
.hs-book__tabs { display: flex; gap: 6px; justify-content: center; flex-wrap: wrap; }
.hs-tab {
  padding: 3px 10px; border: 0; cursor: pointer;
  font-family: ${FDISP}; font-size: max(11px, var(--hm-type-floor)); letter-spacing: 0.08em; text-transform: uppercase;
  color: ${TXT}; background-color: ${W2}; background-image: ${GRAIN};
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.8), inset 0 0 0 2px ${G0};
}
.hs-tab--on { color: #221806;
  background-image: linear-gradient(180deg, #f4e0a3, ${G3} 55%, ${G2});
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.8), 0 0 10px rgba(217,178,90,0.4); }

/* ====================================================== kingdom overview */
.hs-kingdom { display: grid; grid-template-columns: 1fr 320px; gap: 10px;
  padding: 10px; width: 100%; min-height: 0; }
.hs-list { display: flex; flex-direction: column; gap: 6px; overflow-y: auto;
  min-height: 0; padding-right: 4px; }
.hs-listrow {
  display: flex; align-items: center; gap: 10px; padding: 6px 8px;
  width: 100%; cursor: pointer; text-align: left; color: ${TXT};
${reliefDecls("chamfer", "stone", 1)}
}
.hs-listrow:hover { filter: brightness(1.22) saturate(1.06); color: #fff2d0; }
.hs-listrow__name { font-family: ${FDISP}; font-size: max(13px, var(--hm-type-floor)); }
.hs-listrow__meta { font-size: max(12px, var(--hm-type-floor)); line-height: 1.45; color: ${DIM}; }
.hs-flag { width: 8px; align-self: stretch; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.7); }
.hs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(172px, 1fr));
  gap: 6px; }

/* ============================================================ main menu */
.hs-menu { position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 26px; overflow: hidden; }
.hs-menu__sky {
  position: absolute; inset: 0;
  background:
    radial-gradient(70% 55% at 50% 8%, rgba(228,196,122,0.30) 0%, transparent 62%),
    radial-gradient(120% 90% at 50% 120%, rgba(12,10,8,0.95) 0%, transparent 70%),
    linear-gradient(178deg, #16202e 0%, #2a2418 52%, #120c07 100%);
}
/* God rays. Masked to fade out well before the edge: an unmasked conic sweep
   reads as banding artefacts rather than light, which is worse than no rays. */
.hs-menu__rays {
  position: absolute; inset: -20%;
  background-image: repeating-conic-gradient(from 0deg at 50% 22%,
    rgba(255, 232, 180, 0.05) 0deg 4deg, rgba(255, 232, 180, 0) 4deg 12deg);
  mix-blend-mode: screen;
  pointer-events: none;
  -webkit-mask-image: radial-gradient(46% 40% at 50% 22%, #000 0%, transparent 78%);
  mask-image: radial-gradient(46% 40% at 50% 22%, #000 0%, transparent 78%);
}
.hs-menu__art {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.7;
  filter: saturate(0.9) contrast(1.03);
}
.hs-menu__veil {
  position: absolute; inset: 0;
  background:
    radial-gradient(72% 62% at 50% 42%, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.62) 70%, rgba(0,0,0,0.92) 100%),
    linear-gradient(180deg, rgba(8,6,4,0.55) 0%, rgba(8,6,4,0.15) 35%, rgba(8,6,4,0.85) 100%);
}
.hs-menu__title { margin: 0; white-space: pre-line; }
.hs-menu__crest { position: relative; display: flex; gap: 18px; align-items: flex-end; }
.hs-menu__title {
  position: relative;
  font-family: ${FDISP};
  font-size: 58px;
  line-height: 1.02;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-align: center;
  color: #f6e6b8;
  /* No text-shadow. polish.ts fills these letterforms with a gilt gradient
     through background-clip: text and a transparent text fill, so anything set
     here does not sit *behind* the glyphs - it shows straight *through* them.
     That is how a 44px golden bloom ended up painted across the inside of every
     letter: the one Gaussian left in the frame, and it was inside the type. The
     bevel and the cast shadow are drop-shadow filters on the same rule in
     polish.ts, which is the layer that can see the glyph alpha. */
  text-shadow: none;
}
.hs-menu__rule { position: relative; width: 520px; max-width: 80vw; }
.hs-menu__sub { position: relative; font-family: ${FDISP}; font-size: max(14px, var(--hm-type-floor));
  letter-spacing: 0.42em; text-transform: uppercase; color: ${G3}; text-indent: 0.42em; }
.hs-menu__items { position: relative; display: flex; flex-direction: column;
  gap: 10px; min-width: 320px; }
.hs-menu__item {
  padding: 11px 22px;
  font-size: max(15px, var(--hm-type-floor));
  letter-spacing: 0.16em;
}
.hs-menu__foot { position: absolute; bottom: 12px; font-size: max(11px, var(--hm-type-floor)); color: ${FAINT}; }

/* ============================================================ new game */
.hs-setup { display: flex; flex-direction: column; gap: 12px; width: min(940px, 94vw); }
.hs-setup__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.hs-field { display: flex; flex-direction: column; gap: 6px; }
.hs-field__label { font-family: ${FDISP}; font-size: max(11px, var(--hm-type-floor)); letter-spacing: 0.12em;
  /* A shade over the gold token: ${G3} measured 4.44:1 on the panel ground (98,67,43); this is 5.0. */
  text-transform: uppercase; color: #e4be6c; }
.hs-choices { display: flex; gap: 6px; flex-wrap: wrap; }
/* A struck plate, not a browser button. This and \`.hs-input\`, \`.hs-range\` and
   \`.hs-listrow\` below were the four controls still drawing their own flat
   \`inset 0 0 0 1px\` rectangle over \`STONEFACE\` - the last hand-rolled edges in
   this sheet, and the reason a warm oak dialog had four cold rectangles in it.
   They go through the relief primitive like everything else now. */
.hs-choice {
  flex: 1 1 auto; min-width: 78px; padding: 6px 8px; cursor: pointer;
  font-family: ${FDISP}; font-size: max(13px, var(--hm-type-floor)); letter-spacing: 0.04em; color: ${TXT};
  text-shadow: 0 1px 0 rgba(0,0,0,0.85);
${reliefDecls("chamfer", "stone", 1)}
}
.hs-choice:hover { filter: brightness(1.22) saturate(1.06); color: #fff2d0; }
.hs-choice--on { color: #221806;
  background-image: linear-gradient(180deg, #f4e0a3, ${G3} 55%, ${G2});
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.8), 0 0 12px rgba(217,178,90,0.35); }
.hs-choice__sub { display: block; font-size: max(10px, var(--hm-type-floor)); opacity: 0.8; }
.hs-factiongrid { display: grid; grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  gap: 6px; }
.hs-faction { position: relative; padding: 0; border: 0; cursor: pointer;
  background: ${S0}; box-shadow: inset 0 0 0 1px rgba(0,0,0,0.9); overflow: hidden; }
/* The town's own painting behind its keep: a faction is chosen by looking at
   it, and twelve landscapes alone all read the same at thumbnail size. */
.hs-faction__scene { position: relative; display: block; height: 90px; overflow: hidden; }
.hs-faction__bg { position: absolute; left: 50%; top: 0; transform: translateX(-50%);
  filter: brightness(0.82) saturate(0.9); }
.hs-faction__crest { position: absolute; left: 50%; bottom: -4px;
  transform: translateX(-50%); filter: drop-shadow(0 2px 3px rgba(0,0,0,0.8)); }
.hs-faction--on .hs-faction__bg { filter: none; }
.hs-faction__cap { display: block; padding: 3px 4px; font-size: max(11px, var(--hm-type-floor)); color: ${TXT};
  background: linear-gradient(180deg, rgba(20,14,6,0.6), rgba(8,5,2,0.9)); }
.hs-faction--on { box-shadow: inset 0 0 0 2px ${G3}, 0 0 14px rgba(217,178,90,0.45); }
.hs-faction--on .hs-faction__cap { color: #ffeec4; }
.hs-input {
  padding: 6px 8px; width: 100%;
  font-family: ${FNUM}; font-size: max(14px, var(--hm-type-floor)); color: ${TXT};
${reliefDecls("recess", "stone", 1)}
}
.hs-input:focus { outline: none; filter: brightness(1.3); color: #fff2d0; }

/* ============================================================== slider */
.hs-range { width: 100%; height: 22px; background: transparent; -webkit-appearance: none;
  appearance: none; }
.hs-range:focus { outline: none; }
.hs-range::-webkit-slider-runnable-track { height: 8px;
${reliefDecls("recess", "stone", 1)}
}
.hs-range::-moz-range-track { height: 8px;
${reliefDecls("recess", "stone", 1)}
}
.hs-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none;
  width: 14px; height: 20px; margin-top: -6px;
  background-image: linear-gradient(180deg, #f4e0a3, ${G3} 50%, ${G2});
  box-shadow: inset 0 0 0 1px ${G0}, 0 1px 3px rgba(0,0,0,0.8); }
.hs-range::-moz-range-thumb { width: 14px; height: 20px; border: 0;
  background-image: linear-gradient(180deg, #f4e0a3, ${G3} 50%, ${G2});
  box-shadow: inset 0 0 0 1px ${G0}, 0 1px 3px rgba(0,0,0,0.8); }

/* ======================================================= battle result */
.hs-result { display: flex; flex-direction: column; align-items: center; gap: 12px;
  width: min(1000px, 94vw); }
.hs-result__banner {
  font-family: ${FDISP}; font-size: 34px; letter-spacing: 0.16em; text-transform: uppercase;
  color: #f6e6b8;
  text-shadow: 0 2px 0 #6b4d15, 0 4px 0 #2f2109, 0 6px 20px rgba(0,0,0,0.9);
}
.hs-result__sides { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; width: 100%; }
.hs-casualty { display: flex; align-items: center; gap: 8px; padding: 3px 4px; }
.hs-casualty__name { flex: 1 1 auto; min-width: 0; overflow: hidden;
  text-overflow: ellipsis; white-space: nowrap; font-size: max(12px, var(--hm-type-floor)); }
.hs-casualty__num { font-family: ${FNUM}; font-size: max(12px, var(--hm-type-floor)); }
.hs-casualty__num--lost { color: ${BAD}; }

/* ================================================================= misc */
.hs-col { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.hs-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
.hs-muted { color: ${DIM}; }
/* The explanatory note under a control, and the one piece of type an art
   director stopped on: "The classic never sets 11px grey body copy anywhere." It was
   11px in the browser body serif, which made three type families on the
   Settings screen - display serif headings, this, and monospace keycaps.
   Two changes, and the second matters more than the first: 12px with a real
   line-height so it is prose rather than fine print, and the DISPLAY family, so
   a screen has one serif on it instead of two that differ by just enough to
   look like a mistake.

   \`--hm-fs-small\` in \`tokens.ts\` is the other 11px, and it is deliberately NOT
   moved with this one: it sizes \`.hm-btn\`, \`.hm-label\` and \`.hm-status\` in the
   adventure HUD, whose column is 236 design px wide, and a millimetre there is
   a layout change on somebody else's screen rather than a typographic one. */
/* 1.34, not the 1.45 this started at. 1.45 is the right leading for a column of
   prose and the wrong one here: measured at 1366x768, the extra 2.5px a line
   cost across the ten wrapped note lines on the Settings sheet pushed the third
   card past the bottom edge, and a card clipped at rest is a worse defect than
   the one being fixed. 12px at 1.34 still clears the old 11px at the browser's
   default by 3.7px a line. */
.hs-tiny {
  font-family: ${FDISP};
  font-size: max(12px, var(--hm-type-floor));
  line-height: 1.34;
}
.hs-num { font-family: ${FNUM}; }
.hs-clip { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
.hs-scroll { overflow-y: auto; min-height: 0; scrollbar-width: thin;
  scrollbar-color: ${G1} ${S0}; }
.hs-scroll::-webkit-scrollbar { width: 10px; }
.hs-scroll::-webkit-scrollbar-track { background: ${S0}; }
.hs-scroll::-webkit-scrollbar-thumb { background-image: linear-gradient(180deg, ${G2}, ${G1});
  box-shadow: inset 0 0 0 1px ${G0}; }
.hs-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }

@media (max-width: 1279px) {
  .hs-hero { grid-template-columns: 260px 1fr 260px; }
  .hs-book__page { width: 320px; }
  .hs-menu__title { font-size: 46px; }
}

/* A fixed 366px page leaves the spellbook as a postcard in the middle of a
   1080p screen. One coarse step up - never a fractional one, so the icons stay
   on whole pixels - and the book carries the screen it is on. */
@media (min-width: 1600px) and (min-height: 900px) {
  .hs-book__page { width: 512px; padding: 24px 26px; }
  .hs-book__gutter { width: 22px; }
  .hs-book__head { font-size: max(15px, var(--hm-type-floor)); }
  .hs-spell__name { font-size: max(15px, var(--hm-type-floor)); }
  .hs-spell__meta { font-size: max(12px, var(--hm-type-floor)); }
  .hs-spell__cost { font-size: max(17px, var(--hm-type-floor)); }
}
`;
